/**
 * StoryGraphViewer - Hierarchical tree view for Manager to navigate story
 * Shows passages as a left-to-right tree with connecting lines
 * Supports pan/drag, zoom controls, pinch-to-zoom, and collapse/expand
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, Target, Eye, Move, ZoomIn, ZoomOut, Maximize2, Plus, Minus, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

// Node dimensions
const NODE_WIDTH = 140;
const NODE_HEIGHT = 55;
const HORIZONTAL_GAP = 50;
const VERTICAL_GAP = 15;
const MIN_SUBTREE_HEIGHT = NODE_HEIGHT + VERTICAL_GAP;

// Zoom presets
const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200, 250, 300];
const ZOOM_STEP = 25;
const MIN_ZOOM = 25;
const MAX_ZOOM = 300;

const StoryGraphViewer = ({ 
  passages = [], 
  focusNodeId, 
  onSetFocus,
  isExpanded,
  onToggleExpand 
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Pan state
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 20, y: 20 });
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [startOffset, setStartOffset] = useState({ x: 0, y: 0 });
  
  // Zoom state
  const [zoom, setZoom] = useState(100);
  const [showZoomDropdown, setShowZoomDropdown] = useState(false);
  
  // Pinch zoom state
  const [lastPinchDistance, setLastPinchDistance] = useState(null);
  
  // Collapsed nodes state
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  
  // Layout state - calculated via backend API for IP protection
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 200 });
  const [nodeChildrenMap, setNodeChildrenMap] = useState(new Map());
  const [isCalculatingLayout, setIsCalculatingLayout] = useState(false);

  // Toggle collapse
  const toggleCollapse = useCallback((nodeId, e) => {
    e.stopPropagation();
    setCollapsedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Parse links from passage content
  const parseLinks = (content) => {
    const links = [];
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    let match;
    while ((match = linkRegex.exec(content || '')) !== null) {
      const linkContent = match[1];
      let display, target;
      if (linkContent.includes('|')) {
        [display, target] = linkContent.split('|');
      } else if (linkContent.includes('->')) {
        [display, target] = linkContent.split('->');
      } else {
        display = target = linkContent;
      }
      links.push({ display: display.trim(), target: target.trim() });
    }
    return links;
  };

  // Calculate layout via backend API for IP protection
  useEffect(() => {
    const calculateLayoutViaAPI = async () => {
      if (!passages || passages.length === 0) {
        setNodes([]);
        setEdges([]);
        setCanvasSize({ width: 400, height: 200 });
        setNodeChildrenMap(new Map());
        return;
      }

      setIsCalculatingLayout(true);

      try {
        const response = await fetch(`${API_BASE_URL}/api/graph/layout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nodes: passages.map(p => ({
              id: p.id,
              name: p.title || p.id,
              title: p.title || p.id,
              body: p.content || ''
            })),
            collapsed_nodes: Array.from(collapsedNodes),
            node_width: NODE_WIDTH,
            node_height: NODE_HEIGHT,
            horizontal_gap: HORIZONTAL_GAP,
            vertical_gap: VERTICAL_GAP
          })
        });

        if (!response.ok) {
          throw new Error(`Layout API error: ${response.status}`);
        }

        const data = await response.json();
        
        // Transform API response to component format
        const transformedNodes = data.nodes.map(n => {
          const passage = passages.find(p => p.id === n.id);
          return {
            id: n.id,
            title: passage?.title || n.name,
            x: n.x,
            y: n.y,
            links: parseLinks(passage?.content),
            isFocus: n.id === focusNodeId,
            isCollapsed: n.is_collapsed,
            hasChildren: n.has_children,
            childCount: n.child_count
          };
        });

        const transformedEdges = data.edges.map(e => ({
          from: { x: e.from_x, y: e.from_y },
          to: { x: e.to_x, y: e.to_y },
          fromId: e.source,
          toId: e.target,
          isReturn: e.is_return
        }));

        // Build children map from API response
        const childrenMap = new Map();
        data.nodes.forEach(n => {
          if (n.children && n.children.length > 0) {
            childrenMap.set(n.id, n.children);
          }
        });

        setNodes(transformedNodes);
        setEdges(transformedEdges);
        setCanvasSize(data.canvas_size);
        setNodeChildrenMap(childrenMap);

      } catch (error) {
        console.error('Layout API error, using fallback:', error);
        // Fallback: simple vertical layout
        const fallbackNodes = passages.map((p, index) => ({
          id: p.id,
          title: p.title || p.id,
          x: 10,
          y: index * (NODE_HEIGHT + VERTICAL_GAP),
          links: parseLinks(p.content),
          isFocus: p.id === focusNodeId,
          isCollapsed: collapsedNodes.has(p.id),
          hasChildren: false,
          childCount: 0
        }));
        
        setNodes(fallbackNodes);
        setEdges([]);
        setCanvasSize({ 
          width: 200, 
          height: passages.length * (NODE_HEIGHT + VERTICAL_GAP) + 50 
        });
        setNodeChildrenMap(new Map());
      } finally {
        setIsCalculatingLayout(false);
      }
    };

    calculateLayoutViaAPI();
  }, [passages, focusNodeId, collapsedNodes]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleZoomSelect = useCallback((value) => {
    setZoom(value);
    setShowZoomDropdown(false);
  }, []);

  // Pan handlers
  const handleMouseDown = (e) => {
    if (e.target === canvasRef.current || e.target.tagName === 'svg' || e.target.closest('.canvas-bg')) {
      setIsPanning(true);
      setStartPan({ x: e.clientX, y: e.clientY });
      setStartOffset({ ...panOffset });
    }
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    const dx = e.clientX - startPan.x;
    const dy = e.clientY - startPan.y;
    setPanOffset({ x: startOffset.x + dx, y: startOffset.y + dy });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsPanning(true);
      setStartPan({ x: touch.clientX, y: touch.clientY });
      setStartOffset({ ...panOffset });
    } else if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setLastPinchDistance(distance);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isPanning) {
      const touch = e.touches[0];
      const dx = touch.clientX - startPan.x;
      const dy = touch.clientY - startPan.y;
      setPanOffset({ x: startOffset.x + dx, y: startOffset.y + dy });
    } else if (e.touches.length === 2 && lastPinchDistance) {
      e.preventDefault();
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const delta = distance - lastPinchDistance;
      const zoomDelta = delta * 0.5;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + zoomDelta)));
      setLastPinchDistance(distance);
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
    setLastPinchDistance(null);
  };

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -10 : 10;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta)));
    }
  }, []);

  // Reset view
  const resetView = () => {
    setPanOffset({ x: 20, y: 20 });
    setZoom(100);
  };

  // Center on focus node
  const centerOnFocus = () => {
    const focusNode = nodes.find(n => n.id === focusNodeId);
    if (focusNode && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const scale = zoom / 100;
      setPanOffset({
        x: containerWidth / 2 - (focusNode.x + NODE_WIDTH / 2) * scale,
        y: containerHeight / 2 - (focusNode.y + NODE_HEIGHT / 2) * scale
      });
    }
  };

  if (!isExpanded) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg">
        <button
          onClick={onToggleExpand}
          className="w-full flex items-center justify-between p-3 hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-orange-500" />
            <span className="font-medium text-sm">Story Navigator</span>
            <span className="text-xs text-slate-500">({passages.length} passages)</span>
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg">
      {/* Header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 border-b border-slate-200 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-orange-500" />
          <span className="font-medium text-sm">Story Navigator</span>
          <span className="text-xs text-slate-500">({passages.length} passages)</span>
          {collapsedNodes.size > 0 && (
            <span className="text-xs text-amber-600">({collapsedNodes.size} collapsed)</span>
          )}
        </div>
        <ChevronUp className="h-4 w-4 text-slate-400" />
      </button>

      {/* Zoom & Controls Bar */}
      <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-600 flex items-center gap-1">
          <Move className="h-3 w-3" />
          <span className="hidden sm:inline">Drag to pan • Click [-] to collapse</span>
          <span className="sm:hidden">Pinch to zoom</span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Zoom Out */}
          <button
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-1 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Zoom Out"
          >
            <ZoomOut className="h-3.5 w-3.5 text-slate-600" />
          </button>
          
          {/* Zoom Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowZoomDropdown(!showZoomDropdown)}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 min-w-[55px] justify-center text-xs font-medium text-slate-700"
            >
              {Math.round(zoom)}%
              <ChevronDown className="w-2.5 h-2.5" />
            </button>
            
            {showZoomDropdown && (
              <div className="absolute top-full mt-1 right-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 min-w-[80px] max-h-48 overflow-y-auto">
                {ZOOM_PRESETS.map(preset => (
                  <button
                    key={preset}
                    onClick={() => handleZoomSelect(preset)}
                    className={`w-full px-2 py-1 text-left text-xs hover:bg-slate-100 ${
                      Math.round(zoom) === preset ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700'
                    }`}
                  >
                    {preset}%
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Zoom In */}
          <button
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-1 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Zoom In"
          >
            <ZoomIn className="h-3.5 w-3.5 text-slate-600" />
          </button>
          
          {/* Reset */}
          <button
            onClick={resetView}
            className="p-1 rounded hover:bg-slate-200 ml-1"
            title="Reset View"
          >
            <Maximize2 className="h-3.5 w-3.5 text-slate-600" />
          </button>
          
          {/* Expand All */}
          {collapsedNodes.size > 0 && (
            <button
              onClick={() => setCollapsedNodes(new Set())}
              className="p-1 rounded hover:bg-slate-200 text-amber-600"
              title="Expand All"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
          
          {/* Go to Focus */}
          <button
            onClick={centerOnFocus}
            className="text-xs px-2 py-1 bg-orange-100 border border-orange-300 rounded hover:bg-orange-200 text-orange-700 ml-1"
          >
            FOCUS
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden bg-white"
        style={{ 
          height: '220px', 
          cursor: isPanning ? 'grabbing' : 'grab',
          touchAction: 'none'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        {/* Grid background */}
        <div 
          className="canvas-bg absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)',
            backgroundSize: `${15 * (zoom / 100)}px ${15 * (zoom / 100)}px`,
            backgroundPosition: `${panOffset.x}px ${panOffset.y}px`
          }}
        />
        
        <div
          ref={canvasRef}
          className="absolute"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom / 100})`,
            transformOrigin: '0 0',
            width: canvasSize.width,
            height: canvasSize.height
          }}
        >
          {/* Connection lines */}
          <svg
            className="absolute top-0 left-0 pointer-events-none"
            width={canvasSize.width}
            height={canvasSize.height}
          >
            <defs>
              <marker
                id="arrowhead-story"
                markerWidth="8"
                markerHeight="6"
                refX="7"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 8 3, 0 6" fill="#667eea" />
              </marker>
              <marker
                id="arrowhead-return-story"
                markerWidth="8"
                markerHeight="6"
                refX="7"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" opacity="0.6" />
              </marker>
            </defs>
            {edges.map((edge, i) => {
              const midX = (edge.from.x + edge.to.x) / 2;
              return (
                <path
                  key={i}
                  d={`M ${edge.from.x} ${edge.from.y} C ${midX} ${edge.from.y}, ${midX} ${edge.to.y}, ${edge.to.x} ${edge.to.y}`}
                  fill="none"
                  stroke={edge.isReturn ? '#94a3b8' : '#667eea'}
                  strokeWidth="2"
                  strokeDasharray={edge.isReturn ? '4,4' : 'none'}
                  markerEnd={edge.isReturn ? 'url(#arrowhead-return-story)' : 'url(#arrowhead-story)'}
                  opacity={edge.isReturn ? 0.5 : 0.7}
                />
              );
            })}
          </svg>

          {/* Nodes */}
          {nodes.map((node) => (
            <div
              key={node.id}
              className={`
                absolute flex flex-col justify-center px-2 rounded-lg cursor-pointer
                transition-all duration-150 select-none
                ${node.isFocus 
                  ? 'bg-orange-100 border-[3px] border-orange-500 shadow-lg z-10' 
                  : 'bg-white border-2 border-slate-300 hover:border-slate-400 hover:shadow'
                }
              `}
              style={{
                left: node.x,
                top: node.y,
                width: NODE_WIDTH,
                height: NODE_HEIGHT
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSetFocus(node.id);
              }}
            >
              {/* Focus indicator */}
              {node.isFocus && (
                <div className="absolute -top-2 -right-2 bg-orange-500 rounded-full p-0.5 shadow">
                  <Eye className="h-2.5 w-2.5 text-white" />
                </div>
              )}
              
              {/* Collapse/Expand button */}
              {node.hasChildren && (
                <button
                  onClick={(e) => toggleCollapse(node.id, e)}
                  className="absolute -top-1.5 -left-1.5 bg-slate-600 hover:bg-slate-700 rounded-full w-4 h-4 flex items-center justify-center shadow z-20"
                  title={node.isCollapsed ? `Expand (${node.childCount} hidden)` : 'Collapse'}
                >
                  {node.isCollapsed ? (
                    <Plus className="h-2.5 w-2.5 text-white" />
                  ) : (
                    <Minus className="h-2.5 w-2.5 text-white" />
                  )}
                </button>
              )}
              
              {/* Node content */}
              <div className="font-medium text-xs truncate text-center" title={node.title}>
                {node.title}
              </div>
              <div className="flex items-center justify-center gap-1">
                {node.links.length > 0 && (
                  <span className="text-[10px] text-slate-500">→ {node.links.length}</span>
                )}
                {node.isCollapsed && node.childCount > 0 && (
                  <span className="text-[10px] text-amber-600 font-medium">+{node.childCount}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">
            No passages found in story
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-3 py-2 border-t border-slate-200 flex items-center gap-4 text-xs text-slate-600 flex-wrap">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 border-2 border-orange-500 rounded bg-orange-100"></div>
          <span>FOCUS</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-slate-600 rounded-full flex items-center justify-center">
            <Minus className="h-2 w-2 text-white" />
          </div>
          <span>Collapse</span>
        </div>
        <div className="flex items-center gap-1">
          <svg width="16" height="8"><path d="M 0 4 L 12 4" stroke="#667eea" strokeWidth="2"/></svg>
          <span>Link</span>
        </div>
      </div>
      
      {/* Click outside to close dropdown */}
      {showZoomDropdown && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setShowZoomDropdown(false)}
        />
      )}
    </div>
  );
};

export default StoryGraphViewer;
